import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api';

interface User {
  id: number;
  username: string;
  email: string | null;
  fullName: string | null;
  role: 'admin' | 'user';
  isActive: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      console.log('[AuthContext] Checking authentication...');
      const currentUser = await authApi.getCurrentUser();
      console.log('[AuthContext] User authenticated:', currentUser);
      setUser(currentUser);
    } catch (error) {
      console.log('[AuthContext] Not authenticated:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    console.log('[AuthContext] Logging in...');
    const result = await authApi.login(username, password);
    console.log('[AuthContext] Login successful:', result.user);
    setUser(result.user);
  };

  const logout = async () => {
    console.log('[AuthContext] Logging out...');
    await authApi.logout();
    setUser(null);
    console.log('[AuthContext] Logged out');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
